import { Component, inject, OnInit } from '@angular/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import { KwdCurrencyPipe } from '../../../pipes';
import { MatIconModule } from '@angular/material/icon';
import {
  CacBaseDialogComponent,
  CacButtonComponent,
  CacTableComponent,
  table,
} from '@controladad/ng-base';
import { VendorOrderDetailEntity, VendorOrderItemDetail, OrderItemStatus } from '@models';
import { OrderApiService } from '../../../services';
import { OrderStatusValues, OrderItemStatusValues } from '../../../data';
import { BehaviorSubject, tap, map } from 'rxjs';

export interface VendorOrderDetailDialogData {
  orderId: number;
  orderDetail: VendorOrderDetailEntity;
}

export type VendorOrderDetailDialogResult = boolean;

@Component({
  selector: 'lib-vendor-order-detail-dialog',
  standalone: true,
  imports: [
    CacButtonComponent,
    CacTableComponent,
    MatIconModule,
    AsyncPipe,
    DatePipe,
    KwdCurrencyPipe,
  ],
  templateUrl: './vendor-order-detail-dialog.component.html',
  styleUrl: './vendor-order-detail-dialog.component.scss',
})
export class VendorOrderDetailDialogComponent
  extends CacBaseDialogComponent<VendorOrderDetailDialogData, VendorOrderDetailDialogResult>
  implements OnInit {
  private orderApi = inject(OrderApiService);
  private kwdCurrencyPipe = new KwdCurrencyPipe();

  orderDetail$ = new BehaviorSubject<VendorOrderDetailEntity | null>(null);
  loading = true;
  selectedTabIndex = 0;

  OrderStatusValues = OrderStatusValues;

  // Order items table
  orderItemsTable = table({
    itemsFn: () => {
      return this.orderApi.vendorGetOrderDetails(this.data.orderId).pipe(
        tap((order: VendorOrderDetailEntity) => {
          this.orderDetail$.next(order);
        }),
        map((order: VendorOrderDetailEntity) => order.items || [])
      );
    },
    pagination: false,
    selectable: true,
    view: {
      title: 'Order Items',
    },
    columns: {
      'productImage.url': {
        label: 'Image',
        type: 'image',
      },
      productName: {
        label: 'Product Name',
        transform: (_value, item: VendorOrderItemDetail) => this.getProductName(item),
      },
      variationTerms: {
        label: 'Variations',
        transform: (_value, item: VendorOrderItemDetail) => {
          if (!item.variationTerms || item.variationTerms.length === 0) return '-';
          return item.variationTerms.map(variation => {
            const termName = variation.term?.english || variation.term?.arabic || 'Unknown';
            return termName;
          }).join(', ');
        },
      },
      orderedQuantity: {
        label: 'Quantity',
        type: 'number',
      },
      productPrice: {
        label: 'Unit Price',
        type: 'text',
        transform: (value: number | null) => value ? this.kwdCurrencyPipe.transform(value) : 'Gift',
      },
      totalPrice: {
        label: 'Total Price',
        type: 'text',
        transform: (_value, item: VendorOrderItemDetail) => item.productPrice ? this.kwdCurrencyPipe.transform(item.productPrice * item.orderedQuantity) : 'Gift',
      },
      vendorOrderStatus: {
        label: 'Status',
        type: 'tag',
        items: OrderItemStatusValues,
      },
    },
    actions: [
      {
        type: 'button',
        content: 'Change Status',
        action: (item: VendorOrderItemDetail) => {
          // Check if item is cancelled - vendors cannot change status of cancelled items
          if (item.vendorOrderStatus === OrderItemStatus.Cancelled) {
            snackbar$.show('Cannot change status of cancelled items');
            return;
          }

          const variantId = this.getVariantId(item);
          if (!variantId) {
            snackbar$.show('Item ID not available');
            console.error('No variant ID found for item:', item);
            return;
          }

          const productName = this.getProductName(item);
          // Handle both numeric and string status values
          const newStatus = item.vendorOrderStatus === OrderItemStatus.Ready ? OrderItemStatus.InProgress : OrderItemStatus.Ready;
          const actionText = newStatus === OrderItemStatus.Ready ? 'Ready' : 'In Progress';
          const buttonText = newStatus === OrderItemStatus.Ready ? 'Mark as Ready' : 'Mark as In Progress';

          console.log(`Current status: ${item.vendorOrderStatus}, New status: ${newStatus}`);

          return dialog$
            .prompt({
              title: buttonText,
              message: `Are you sure you want to mark "${productName}" as ${actionText}?`,
              yesButtonText: `Yes, ${buttonText}`,
              noButtonText: 'Cancel',
            })
            .action(() => {
              return this.orderApi.vendorUpdateVariantStatus(
                this.data.orderId,
                [variantId],
                newStatus
              ).pipe(
                tap(() => {
                  snackbar$.show(`Item marked as ${actionText} successfully`);
                  this.orderItemsTable.refresh();
                })
              );
            });
        },
      },
    ],
    bulkActions: [
      {
        content: 'Mark as In Progress',
        action: (selectedItems: VendorOrderItemDetail[]) => {
          if (selectedItems.length === 0) {
            snackbar$.show('No valid items selected');
            return;
          }

          // Filter out cancelled items - vendors cannot change status of cancelled items
          const validItems = selectedItems.filter(item => item.vendorOrderStatus !== OrderItemStatus.Cancelled);
          if (validItems.length === 0) {
            snackbar$.show('Cannot change status of cancelled items');
            return;
          }

          if (validItems.length < selectedItems.length) {
            snackbar$.show(`${selectedItems.length - validItems.length} cancelled item(s) will be skipped`);
          }

          const variantIds = validItems.map(item => this.getVariantId(item)!).filter(id => id !== null);
          return dialog$
            .prompt({
              title: 'Mark Items as In Progress',
              message: `Are you sure you want to mark ${validItems.length} item(s) as in progress?`,
              yesButtonText: 'Yes, Mark as In Progress',
              noButtonText: 'Cancel',
            })
            .action(() => {
              return this.orderApi.vendorUpdateVariantStatus(
                this.data.orderId,
                variantIds,
                OrderItemStatus.InProgress
              );
            });
        },
      },
      {
        content: 'Mark as Ready',
        action: (selectedItems: VendorOrderItemDetail[]) => {
          if (selectedItems.length === 0) {
            snackbar$.show('No valid items selected');
            return;
          }

          // Filter out cancelled items - vendors cannot change status of cancelled items
          const validItems = selectedItems.filter(item => item.vendorOrderStatus !== OrderItemStatus.Cancelled);
          if (validItems.length === 0) {
            snackbar$.show('Cannot change status of cancelled items');
            return;
          }

          if (validItems.length < selectedItems.length) {
            snackbar$.show(`${selectedItems.length - validItems.length} cancelled item(s) will be skipped`);
          }

          const variantIds = validItems.map(item => this.getVariantId(item)!).filter(id => id !== null);
          return dialog$
            .prompt({
              title: 'Mark Items as Ready',
              message: `Are you sure you want to mark ${validItems.length} item(s) as ready?`,
              yesButtonText: 'Yes, Mark as Ready',
              noButtonText: 'Cancel',
            })
            .action(() => {
              return this.orderApi.vendorUpdateVariantStatus(
                this.data.orderId,
                variantIds,
                OrderItemStatus.Ready
              );
            });
        },
      },
    ],
  });



  ngOnInit() {
    // Order detail is always pre-loaded (like admin dialog)
    this.orderDetail$.next(this.data.orderDetail);
    this.loading = false;
  }

  // Helper methods
  getProductName(item: VendorOrderItemDetail): string {
    return item.productName?.english || item.productName?.arabic || 'Unknown Product';
  }

  getVariantId(item: VendorOrderItemDetail): number | null {
    // Try different possible field names for the variant ID
    return item.id || item.variantId || (item as any).productVariantId || (item as any).orderItemId || null;
  }

  // Get status display name for vendors (read-only)
  getStatusDisplayName(status: number): string {
    const statusItem = OrderStatusValues.find(s => s.value === status);
    return statusItem?.label || 'Unknown Status';
  }

  // Tab management
  selectTab(index: number) {
    this.selectedTabIndex = index;
  }

  // Mark entire order as ready
  markOrderAsReady(): void {
    const orderDetail = this.orderDetail$.value;
    if (!orderDetail) {
      snackbar$.show('Order details not available');
      return;
    }

    dialog$
      .prompt({
        title: 'Mark Order as Ready',
        message: `Are you sure you want to mark the entire order #${orderDetail.id} as ready?`,
        yesButtonText: 'Yes, Mark as Ready',
        noButtonText: 'Cancel',
      })
      .action(() => {
        return this.orderApi.vendorMarkOrderAsReady(orderDetail.id).pipe(
          tap(() => {
            snackbar$.show('Order marked as ready successfully');
            this.orderItemsTable.refresh();
          })
        );
      });
  }



  closeDialog() {
    this.close(true);
  }
}
